import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { formatDisplayDateTime } from '@/utils/date';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import type {
  PcmpRequestMineItem,
  PcmpRequestQueueItem,
  PcmpRequestSummary,
} from '../../types/pcmpRequest.types';
import { PcmpEmptyCell, PersonCell } from '../PcmpQueue/PcmpQueueCells';
import { getPcmpQueueColumns } from '../PcmpQueue/pcmpQueueColumns';
import { PcmpRequestStatusPill } from './PcmpRequestStatusPill';

export function pcmpPaymentSourceColumn<T extends PcmpRequestQueueItem>(): ColumnDef<T> {
  return {
    id: 'paymentSource',
    header: 'Payment Source',
    cell: ({ row }) => (
      <span className="whitespace-nowrap">{PCMP_CHANNEL_LABELS[row.original.paymentSource]}</span>
    ),
  };
}

/** What a request carries: its category, status, who raised it and the decision. */
export function pcmpRequestSummaryColumns<
  T extends { request: PcmpRequestSummary },
>(): ColumnDef<T>[] {
  return [
    {
      id: 'requestCategory',
      header: 'Request',
      cell: ({ row }) => (
        <div className="flex max-w-56 flex-col" title={row.original.request.note ?? undefined}>
          <span className="truncate font-medium">{row.original.request.categoryName}</span>
          {row.original.request.note ? (
            <span className="truncate text-xs text-muted-foreground">
              {row.original.request.note}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'requestStatus',
      header: 'Request Status',
      cell: ({ row }) => <PcmpRequestStatusPill status={row.original.request.status} />,
    },
    {
      id: 'raised',
      header: 'Raised',
      cell: ({ row }) => (
        <div className="flex max-w-48 flex-col gap-0.5">
          <PersonCell person={row.original.request.raisedBy} />
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            {formatDisplayDateTime(row.original.request.raisedAt)}
          </span>
        </div>
      ),
    },
    {
      id: 'decision',
      header: 'Decision',
      cell: ({ row }) => {
        const { request } = row.original;
        if (!request.decidedAt) return <PcmpEmptyCell />;
        return (
          <div className="flex max-w-56 flex-col gap-0.5">
            <PersonCell person={request.decidedBy} />
            <span className="text-xs whitespace-nowrap text-muted-foreground">
              {formatDisplayDateTime(request.decidedAt)}
            </span>
            {request.decisionComment ? (
              <span className="truncate text-xs" title={request.decisionComment}>
                {request.decisionComment}
              </span>
            ) : null}
          </div>
        );
      },
    },
  ];
}

interface PcmpRequestColumnOptions<T> {
  leading?: ColumnDef<T>[] | undefined;
  renderActions: (item: T) => ReactNode;
}

/** The Standard and Disbursed columns: the queue's, with the payment source after the amounts. */
export function getPcmpRequestColumns<T extends PcmpRequestQueueItem = PcmpRequestQueueItem>({
  leading,
  renderActions,
}: PcmpRequestColumnOptions<T>): ColumnDef<T>[] {
  return getPcmpQueueColumns<T>({
    leading,
    afterAmounts: [pcmpPaymentSourceColumn<T>()],
    renderActions,
  });
}

/** The My Requests columns: the same, plus the request's own. */
export function getPcmpRequestMineColumns({
  leading,
  renderActions,
}: PcmpRequestColumnOptions<PcmpRequestMineItem>): ColumnDef<PcmpRequestMineItem>[] {
  return getPcmpQueueColumns<PcmpRequestMineItem>({
    leading,
    afterAmounts: [pcmpPaymentSourceColumn()],
    afterLanes: pcmpRequestSummaryColumns<PcmpRequestMineItem>(),
    renderActions,
  });
}
